import React, { useEffect, useRef, useState, useId } from "react";
import '../../styles/about/roadmap.css';

export default function Roadmap() {
	const [active, setActive] = useState(null);
	const [isAuto, setIsAuto] = useState(true);         // auto-cycle on/off
	const id = useId();
	const wrapRef = useRef(null);
	const resumeTimerRef = useRef(null);                // resume-after-3s timer
	const cycleIntervalRef = useRef(null);              // cycling interval

	const steps = React.useMemo(() => [
		{ id: "WP1", year: "Year 1", yearKey: "y1", title: "Harmonized Platform Development", blurb: "Create standardized methods for clinical proteomics and benchmark performance across Sweden." },
		{ id: "WP2", year: "Year 1", yearKey: "y1", title: "Tumor-Informed Plasma Proteomics", blurb: "Developing methods for minimal residual disease detection and therapy selection." },
		{ id: "WP3", year: "Year 2", yearKey: "y2", title: "Data Sharing and Reporting", blurb: "Establishing a national framework for data storage, analysis, and reporting." },
		{ id: "WP4", year: "Year 3", yearKey: "y3", title: "Proof-of-Concept Trials", blurb: "Conducting lung and breast cancer trials to validate proteomics methods." },
	], []);

	const byYear = (key) => steps.filter((s) => s.yearKey === key);

	// click-away closes any open bubble
	useEffect(() => {
		const onDocClick = (e) => {
		if (!wrapRef.current) return;
		if (!wrapRef.current.contains(e.target)) setActive(null);
		};
		document.addEventListener("pointerdown", onDocClick);
		return () => document.removeEventListener("pointerdown", onDocClick);
	}, []);

	// ---- Auto-cycling control -----------------------------------------------
	// Start/stop the cycling interval based on isAuto
	useEffect(() => {
		// clear any previous interval
		if (cycleIntervalRef.current) {
		clearInterval(cycleIntervalRef.current);
		cycleIntervalRef.current = null;
		}
		if (!isAuto) return;

		cycleIntervalRef.current = setInterval(() => {
		setActive((current) => {
			if (!current) return 'WP1';
			const idx = steps.findIndex(s => s.id === current);
			return idx === steps.length - 1 ? 'WP1' : steps[idx + 1].id;
		});
		}, 3000);

		return () => {
		if (cycleIntervalRef.current) {
			clearInterval(cycleIntervalRef.current);
			cycleIntervalRef.current = null;
		}
		};
	}, [isAuto, steps]);

	// Helper to PAUSE auto and clear any scheduled resume
	const pauseAuto = () => {
		if (resumeTimerRef.current) {
		clearTimeout(resumeTimerRef.current);
		resumeTimerRef.current = null;
		}
		setIsAuto(false);
	};

	// Helper to schedule RESUME auto 3s after pointer leaves
	const scheduleAutoResume = () => {
		if (resumeTimerRef.current) {
		clearTimeout(resumeTimerRef.current);
		}
		resumeTimerRef.current = setTimeout(() => {
		setIsAuto(true);
		resumeTimerRef.current = null;
		}, 3000);
	};

	// ---- Step subcomponent ----------------------------------------------------
	const Step = (s, i) => {
		const globalIndex = steps.findIndex(step => step.id === s.id);

		const handleUserInteraction = (stepId) => {
		pauseAuto();           // pause cycling immediately on interaction
		setActive(stepId);
		};

		return (
		<div
			key={s.id}
			className="roadmap-step"
			onMouseEnter={() => handleUserInteraction(s.id)}
			onMouseLeave={() => { setActive(null); /* bubble hides when leaving step */ }}
		>
			<button
				className={`roadmap-step-dot roadmap-step-dot--${s.yearKey} ${active === s.id ? "roadmap-step-dot--active" : ""}`}
				aria-expanded={active === s.id}
				aria-controls={`${id}-${s.id}-bubble`}
				onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
				onFocus={() => handleUserInteraction(s.id)}
			>
				<span className="roadmap-step-dot__index">{globalIndex + 1}</span>
				<span className="roadmap-step-dot__label">{s.id}</span>
			</button>

			<article
				id={`${id}-${s.id}-bubble`}
				role="tooltip"
				className={`roadmap-step-bubble ${active === s.id ? "roadmap-step-bubble--active" : ""}`}
				// Keep open while hovering the bubble itself
				onMouseEnter={() => handleUserInteraction(s.id)}
				onMouseLeave={() => setActive(null)}
			>
				<header>
					<div className={`roadmap-step-chip roadmap-step-chip--${s.yearKey}`}>{s.id}</div>
					<h3>{s.title}</h3>
				</header>
				<p>{s.blurb}</p>
				<footer className="roadmap-step-meta">{s.year}</footer>
			</article>
		</div>
		);
	};

	return (
		<section className="roadmap-pro" aria-labelledby={`${id}-heading`}>
			<header className="roadmap-header roadmap-container">
				<h1 id={`${id}-heading`}>How We're Achieving Our Mission</h1>
				<p>
					ProMS is structured into four scientific work packages (WPs). These WPs
					form the foundation for our roadmap—from harmonization to trials and
					impact.
				</p>
			</header>

			<div className="roadmap-years roadmap-container" aria-hidden>
				<div className="roadmap-year roadmap-year--1">Year 1: Harmonized platforms & SOPs</div>
				<div className="roadmap-year roadmap-year--2">Year 2: Clinical trials begin</div>
				<div className="roadmap-year roadmap-year--3">Year 3: Implementation & expansion</div>
			</div>

			<div
				ref={wrapRef}
				className="roadmap-steps roadmap-container"
				onMouseEnter={() => pauseAuto()}
				onMouseLeave={() => {
					setActive(null);
					scheduleAutoResume();
				}}
			>
			<div className="roadmap-steps-column roadmap-steps-column--year1">{byYear("y1").map(Step)}</div>
			<div className="roadmap-steps-column roadmap-steps-column--year2">{byYear("y2").map(Step)}</div>
			<div className="roadmap-steps-column roadmap-steps-column--year3">{byYear("y3").map(Step)}</div>
			</div>

			<div className="roadmap-mobile-stack roadmap-container">
			{steps.map((s) => (
				<article key={`m-${s.id}`} className="roadmap-mobile-card">
				<header>
					<div className={`roadmap-step-chip roadmap-step-chip--${s.yearKey}`}>{s.id}</div>
					<h3>{s.title}</h3>
				</header>
				<p>{s.blurb}</p>
				<footer>{s.year}</footer>
				</article>
			))}
			</div>
		</section>
	);
}
